import { StyledButton } from '@app/components/button/Button';
import useApp from '@app/hooks/useApp';
import { getMetaConfig, loadFacebookSdk } from '@app/integrations/meta/metaSdk';
import api from '@app/services/api';
import { apiEndpoints } from '@app/utils/constants';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

export type MetaConnectButtonProps = {
    location_id?: string | null;
    twilio_sid?: string | null;
};

export const MetaConnectButton = ({ location_id, twilio_sid }: MetaConnectButtonProps) => {
    const { message } = useApp();
    const queryClient = useQueryClient();
    const [isLoading, setIsLoading] = useState(false);
    const { appId } = getMetaConfig();

    const [isConnectedLocal, setIsConnectedLocal] = useState(Boolean(twilio_sid));
    const isConnected = Boolean(twilio_sid) || isConnectedLocal;
    const isConfigured = Boolean(appId);
    const didSendWabaRef = useRef(false);
    const pendingLocationIdRef = useRef<string | null>(null);

    useEffect(() => {
        const listener = (event: MessageEvent) => {
            if (!event.origin.endsWith('facebook.com')) return;
            if (typeof event.data !== 'string') return;
            try {
                const data = JSON.parse(event.data) as {
                    type?: string;
                    event?: string;
                    data?: {
                        phone_number_id?: string;
                        waba_id?: string;
                        current_step?: string;
                        error_message?: string;
                    };
                };

                if (data.type !== 'WA_EMBEDDED_SIGNUP') return;

                if (data.event === 'FINISH' || data.event === 'FINISH_ONLY_WABA') {
                    console.log('WA Embedded Signup FINISH', data.data);

                    const wabaId = data.data?.waba_id;
                    const pendingLocationId = pendingLocationIdRef.current;
                    if (wabaId && pendingLocationId && !didSendWabaRef.current) {
                        didSendWabaRef.current = true;
                        api.post(apiEndpoints.meta.connectWaba, {
                            waba_id: wabaId,
                            location_id: pendingLocationId,
                        })
                            .then(async () => {
                                pendingLocationIdRef.current = null;
                                setIsConnectedLocal(true);
                                await queryClient.invalidateQueries({ queryKey: ['location'] });
                            })
                            .catch((e) => {
                                console.error('Failed to connect WABA', e);
                                didSendWabaRef.current = false;
                            });
                    }
                } else if (data.event === 'CANCEL') {
                    console.warn('WA Embedded Signup CANCEL', data.data);
                } else if (data.event === 'ERROR') {
                    console.error('WA Embedded Signup ERROR', data.data);
                }
            } catch {
                console.error('Failed to parse WA Embedded Signup message', event.data);
            }
        };

        globalThis.addEventListener('message', listener);
        return () => globalThis.removeEventListener('message', listener);
    }, [queryClient]);

    const handleConnect = async () => {
        if (isConnected || !location_id) {
            return;
        }

        if (!isConfigured) {
            message.error({
                type: 'error',
                content: 'Meta Connect is not configured (missing Meta App ID).',
            });
            return;
        }

        setIsLoading(true);
        try {
            await loadFacebookSdk();

            pendingLocationIdRef.current = location_id;
            didSendWabaRef.current = false;

            const configRes = await api.get(apiEndpoints.meta.config);
            const cfg = configRes?.data?.data ?? configRes?.data ?? {};
            const configId = cfg?.config_id as string | undefined;
            const solutionId = cfg?.solution_id as string | undefined;

            if (!configId || !solutionId) {
                setIsLoading(false);
                message.error({
                    type: 'error',
                    content: 'Meta Connect is not configured (missing config/solution on backend).',
                });
                return;
            }

            FB?.login(
                (response: { status?: string; authResponse?: { code?: string } }) => {
                    // requested: log response so we can verify `code`
                    console.log('Meta FB.login response', response);

                    setIsLoading(false);
                },
                {
                    config_id: configId,
                    auth_type: 'rerequest',
                    response_type: 'code',
                    override_default_response_type: true,
                    extras: {
                        sessionInfoVersion: 3,
                        featureType: 'only_waba_sharing',
                        setup: {
                            solutionID: solutionId,
                        },
                    },
                }
            );
        } catch (e) {
            setIsLoading(false);
            pendingLocationIdRef.current = null;
            console.error('Failed to start Meta connection', e);
            message.error({
                type: 'error',
                content: 'Failed to start Meta connection. Please try again.',
            });
        }
    };

    if (isConnected) {
        return (
            <StyledButton type="default" disabled color="green">
                WhatsApp Connected
            </StyledButton>
        );
    }

    if (!location_id) {
        return null;
    }

    return (
        <StyledButton type="primary" color="green" onClick={handleConnect} loading={isLoading}>
            Connect WhatsApp
        </StyledButton>
    );
};

export default MetaConnectButton;
